var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Box from '@redbubble/design-system/src/Box';
import Logo from '@redbubble/design-system/src/Logo';
import { useCookiebotConsent } from '@redbubble/next-analytics';
import { RBProvider } from '@redbubble/next-provider';
import { RudderstackIdentifyTracking } from '@redbubble/next-provider/rudderstack';
import { DefaultSeo } from '@redbubble/next-seo';
import { GoogleTagManagerScript, SiftScript, } from '@redbubble/next-vendor-scripts';
import { isDisabledGtmRoute } from '@redbubble/utils/isDisabledOnRoute';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { AppFooter } from './components/AppFooter';
import { AppHeader } from './components/AppHeader';
import { ErrorBoundary } from './error-boundary';
import { useViewportWidth } from './hooks';
import { Modals } from './modals';
import { DeviceType, } from './types';
var publicRuntimeConfig = getConfig().publicRuntimeConfig;
/**
 * Package: @redbubble/next-layout
 */
// const Header = injectIntl(_Header);
// const Footer = injectIntl(_Footer);
export function RedbubbleLayout(props) {
    var intl = useIntl();
    var _a = publicRuntimeConfig.app, appVersion = _a.version, displayVersion = _a.displayVersion;
    var userInfo = props.userInfo, globalNavigation = props.globalNavigation, deviceType = props.deviceType, bannerMessages = props.bannerMessages, children = props.children, searchTerm = props.searchTerm, searchProductContext = props.searchProductContext, bannerInitialSeconds = props.bannerInitialSeconds, draftMode = props.draftMode, forceMobileHeader = props.forceMobileHeader, disableExpandedMobileSearch = props.disableExpandedMobileSearch, headerAndFooterVariant = props.headerAndFooterVariant, disableStickySearch = props.disableStickySearch, engagingPlaceholderMessages = props.engagingPlaceholderMessages, headerType = props.headerType, hideProductNavigation = props.hideProductNavigation, showAnimatedLogo = props.showAnimatedLogo;
    var isOrAbove = useViewportWidth(deviceType).isOrAbove;
    var getViewport = function () {
        if (isOrAbove(DeviceType.Desktop)) {
            return DeviceType.Desktop;
        }
        return DeviceType.Mobile;
    };
    var customDeviceType = deviceType !== null && deviceType !== void 0 ? deviceType : getViewport();
    return (React.createElement(React.Fragment, null,
        React.createElement(ErrorBoundary, null,
            React.createElement(AppHeader, { intl: intl, forceMobileHeader: forceMobileHeader, userInfo: userInfo, globalNavigation: globalNavigation, bannerMessages: bannerMessages, deviceType: customDeviceType, disableExpandedMobileSearch: disableExpandedMobileSearch, headAndFooterVariant: headerAndFooterVariant, disableStickySearch: disableStickySearch, engagingPlaceholderMessages: engagingPlaceholderMessages, searchTerm: searchTerm, searchProductContext: searchProductContext, type: headerType, hideProductNavigation: hideProductNavigation, showAnimatedLogo: showAnimatedLogo, bannerInitialSeconds: bannerInitialSeconds })),
        children,
        React.createElement(ErrorBoundary, null,
            React.createElement(AppFooter, { intl: intl, userInfo: userInfo, headAndFooterVariant: headerAndFooterVariant })),
        React.createElement(Modals, null),
        draftMode && (React.createElement("a", { href: "/fep/api/exit-preview", style: {
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                padding: '10px',
                backgroundColor: '#ff0000',
                color: 'white',
                textAlign: 'center',
                textDecoration: 'none',
                zIndex: 1000,
                boxShadow: '0 -2px 5px rgba(0,0,0,0.2)',
            } }, "Exit Preview Mode")),
        displayVersion && React.createElement("small", null,
            "v",
            appVersion)));
}
export function MinimalCheckoutHeaderLayout(props) {
    var intl = useIntl();
    var userInfo = props.userInfo, MinimalHeader = props.MinimalHeader;
    return (React.createElement(React.Fragment, null,
        React.createElement(MinimalHeader, null),
        props.children,
        React.createElement(ErrorBoundary, null,
            React.createElement(AppFooter, { intl: intl, userInfo: userInfo }))));
}
export function RedbubbleLiteLayout(_a) {
    // TODO: Add localisation to lite pages (used by static 404 and 500 pages)
    var children = _a.children;
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { textAlign: "center", padding: "xl" },
            React.createElement(Box, { display: "inline-block", style: { maxWidth: 160 } },
                React.createElement("a", { href: "/", title: "Return to homepage" },
                    React.createElement(Logo, null)))),
        children));
}
export function RedbubbleApp(_a) {
    var _b, _c;
    var Component = _a.Component, pageProps = _a.pageProps;
    var Layout = Component.layout || RedbubbleLayout;
    var router = useRouter();
    var _d = useCookiebotConsent(), consent = _d.consent, deniedConsent = _d.deniedConsent, allowedConsent = _d.allowedConsent;
    var gtmContainerURL = (_c = (_b = publicRuntimeConfig === null || publicRuntimeConfig === void 0 ? void 0 : publicRuntimeConfig.gtm) === null || _b === void 0 ? void 0 : _b.containerURL) !== null && _c !== void 0 ? _c : '';
    var enableGTM = !isDisabledGtmRoute(router.pathname);
    var experiments = pageProps.experiments, isBot = pageProps.isBot, amplitudeExperiments = pageProps.amplitudeExperiments;
    var _ = pageProps.amplitudeExperiments, componentProps = __rest(pageProps, ["amplitudeExperiments"]);
    var experimentsAddedToDD = useRef(false);
    useEffect(function () {
        if (window.DD_RUM && !experimentsAddedToDD.current) {
            window.DD_RUM.onReady(function () {
                if (experiments === null || experiments === void 0 ? void 0 : experiments.length) {
                    experiments.forEach(function (_a) {
                        var experiment = _a.experiment, group = _a.group;
                        window.DD_RUM.addFeatureFlagEvaluation(experiment, group);
                    });
                }
            });
            experimentsAddedToDD.current = true;
        }
    }, [experiments]);
    return (React.createElement(RBProvider, { pageProps: pageProps, amplitudeExperiments: amplitudeExperiments, rudderstackProps: {
            configUrl: publicRuntimeConfig.rudderstack.configUrl,
            writeKey: publicRuntimeConfig.rudderstack.writeKey,
            dataplaneUrl: publicRuntimeConfig.rudderstack.dataplaneUrl,
            enabled: consent.statistics && !isBot, // x-rb-bot-identified header
            deniedConsent: deniedConsent,
            allowedConsent: allowedConsent,
        } },
        React.createElement(Head, null,
            React.createElement("meta", { name: "viewport", content: "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=0" })),
        React.createElement(DefaultSeo, null),
        consent.statistics && !isBot && (React.createElement(RudderstackIdentifyTracking, { identifyCallProps: {
                federatedId: String(pageProps.userInfo.federatedId),
                isLoggedIn: pageProps.userInfo.isLoggedIn,
                cloudflare: pageProps.userInfo.cloudflare,
                amplitudeExperiments: amplitudeExperiments,
            } })),
        enableGTM && publicRuntimeConfig.gtm.containerId && (React.createElement(GoogleTagManagerScript, { id: publicRuntimeConfig.gtm.containerId, userProperties: pageProps.userProperties, containerURL: gtmContainerURL })),
        publicRuntimeConfig.sift.publicKey &&
            pageProps.userInfo &&
            pageProps.userInfo.userId && (React.createElement(SiftScript, { userId: pageProps.userInfo.userId, publicKey: publicRuntimeConfig.sift.publicKey })),
        React.createElement(Layout, { userInfo: pageProps.userInfo, deviceType: pageProps.deviceType, globalNavigation: pageProps.globalNavigation, bannerMessages: pageProps.bannerMessages, experiments: pageProps.experiments, searchTerm: pageProps.searchTerm, searchProductContext: pageProps.searchProductContext, bannerInitialSeconds: pageProps.bannerInitialSeconds, draftMode: pageProps.draftMode },
            React.createElement(Component, __assign({}, componentProps)))));
}
